/* RevisionRequestPanel — Planner-facing view of structured revisions.
 *
 * Renders the case's `revision_requests[]` array (newest first) as
 * grouped, scannable cards. Designed for the planner workflow:
 *   - Critical items are pinned at the top of each request (so the
 *     planner addresses the show-stoppers first)
 *   - Each item shows category icon + teeth chips + severity pill
 *   - The doctor's overall summary leads the request (gist before details)
 *   - Iteration count shown so the planner knows how many rounds back
 *
 * iter97.53 — every row now carries a "Mark as actioned" checkbox.
 * When the planner ticks ALL rows, the "Resubmit to doctor →" footer
 * button enables; clicking it POSTs the structured acknowledgement
 * payload and flips the case status to `sent_for_approval` so the
 * doctor sees the revised plan immediately. Structured numeric/chip
 * changes (iter97.52) are surfaced as their own diff card above the
 * free-text items so the planner can see exactly what value the
 * doctor wants vs. the current plan.
 *
 * Backward-compatible: legacy revisions (just a timeline note, no
 * structured payload) show up as a single "Free-form note" card at the
 * top of the SubmissionTab via `legacyNotes` prop.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
    ArrowUUpLeft, GitDiff, Stack, TreeStructure, NotePencil, Warning,
    PaperPlaneTilt, CheckCircle, Ruler, Hash, Crosshair, ArrowSquareOut,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const CATEGORY_LOOKUP = {
    // Legacy free-form categories (still supported)
    ipr:                     { label: "IPR",                    icon: GitDiff },
    attachments:             { label: "Attachments",            icon: Stack },
    sequence:                { label: "Movement sequence",      icon: TreeStructure },
    aligner_count:           { label: "Aligner count",          icon: Stack },
    torque_tip:              { label: "Torque / Tip / Rotation", icon: GitDiff },
    extractions:             { label: "Extraction plan",        icon: Warning },
    retention:               { label: "Retention protocol",     icon: NotePencil },
    other:                   { label: "Other",                  icon: NotePencil },
    // iter97.52 categories from the new full-page Request a Revision flow
    viewer_comment:          { label: "Plan viewer comment",    icon: ArrowSquareOut },
    attachments_revision:    { label: "Attachments",            icon: Stack },
    ipr_revision:            { label: "IPR",                    icon: GitDiff },
    elastics_revision:       { label: "Elastics",               icon: GitDiff },
    rationale_revision:      { label: "Rationale / notes",      icon: NotePencil },
    overcorrection_revision: { label: "Overcorrection",         icon: Warning },
    overall_notes:           { label: "Overall notes",          icon: NotePencil },
    // iter98.99bf — per-tooth + wear-schedule categories
    per_tooth_movements_revision:   { label: "Per-tooth movements",       icon: GitDiff },
    per_tooth_attachments_revision: { label: "Per-tooth attachments",     icon: Stack },
    per_tooth_cuts_revision:        { label: "Per-tooth precision cuts",  icon: GitDiff },
    per_tooth_biteramps_revision:   { label: "Per-tooth bite ramps",      icon: Warning },
    wear_schedule_notes:            { label: "Wear schedule notes",       icon: Ruler },
};

/* Structured field metadata — used to render the doctor's typed values
   as a planner-readable diff. Each entry maps a backend field key to
   a UI label, a unit suffix, and the corresponding key on `case.plan`
   so we can show "current → requested" side-by-side. */
const STRUCT_FIELDS = {
    aligner_count_upper:    { label: "Upper aligners",         unit: "aligners", planKey: "aligner_count_upper",     section: "Aligners & Duration", icon: Hash },
    aligner_count_lower:    { label: "Lower aligners",         unit: "aligners", planKey: "aligner_count_lower",     section: "Aligners & Duration", icon: Hash },
    duration_weeks:         { label: "Duration",               unit: "weeks",    planKey: "estimated_duration_weeks", section: "Aligners & Duration", icon: Ruler },
    wear_days:              { label: "Wear schedule",          unit: "days",     planKey: "change_interval_days",    section: "Aligners & Duration", icon: Ruler },
    overjet_initial_mm:     { label: "Overjet (initial)",      unit: "mm",       planKey: "initial_overjet_mm",      section: "Bite Metrics",        icon: Crosshair },
    overbite_initial_mm:    { label: "Overbite (initial)",     unit: "mm",       planKey: "initial_overbite_mm",     section: "Bite Metrics",        icon: Crosshair },
    final_overjet_mm:       { label: "Final overjet",          unit: "mm",       planKey: "final_overjet_mm",        section: "Bite Metrics",        icon: Crosshair },
    final_overbite_mm:      { label: "Final overbite",         unit: "mm",       planKey: "final_overbite_mm",       section: "Bite Metrics",        icon: Crosshair },
    total_ipr_mm:           { label: "Total IPR",              unit: "mm",       planKey: "total_ipr_mm",            section: "Bite Metrics",        icon: GitDiff },
    retainer_type:          { label: "Retainer type",          unit: "",         planKey: "retainer_type",           section: "Retention Plan",      icon: NotePencil },
    follow_up_recall_months:{ label: "Follow-up recall",       unit: "months",   planKey: "retention_recall_months", section: "Retention Plan",      icon: Ruler },
    wear_protocol_text:     { label: "Wear protocol",          unit: "",         planKey: "retention_schedule",      section: "Retention Plan",      icon: NotePencil },
};

const SEVERITY_CONFIG = {
    critical: { label: "Critical", chip: "bg-rose-100 text-rose-800 ring-1 ring-rose-300" },
    normal:   { label: "Normal",   chip: "bg-amber-100 text-amber-900 ring-1 ring-amber-300" },
    minor:    { label: "Minor",    chip: "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300" },
};

const fmtRelative = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const diff = Math.floor((Date.now() - d.getTime()) / 60000);
        if (diff < 1) return "just now";
        if (diff < 60) return `${diff}m ago`;
        if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
        return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
    } catch { return ""; }
};


/* Hash a tooth array to a short label without overrunning the badge. */
function teethLabel(teeth) {
    if (!teeth?.length) return null;
    if (teeth.length <= 6) return `Teeth: ${teeth.join(", ")}`;
    return `Teeth: ${teeth.length} selected`;
}


export default function RevisionRequestPanel({ caseId, caseStatus, onResubmit }) {
    const { user } = useAuth() || {};
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    /* iter98.99dd — Default collapsed per user instruction. */
    const [expanded, setExpanded] = useState(false);
    const [actioned, setActioned] = useState(new Set());
    const [planSnapshot, setPlanSnapshot] = useState(null);  // current case.plan for diff
    const [submitting, setSubmitting] = useState(false);
    /* iter98.88 — Planner's reply-note composer. Drives the textarea
     * inside the ResubmitFooter; on submit it's posted as `note` so
     * the doctor sees the planner's reasoning when they come back.
     * Empty = no extra note (falls back to a system-style stamp). */
    const [replyNote, setReplyNote] = useState("");

    useEffect(() => {
        let alive = true;
        Promise.all([
            api.get(`/cases/${caseId}/revision-requests`).then((r) => r.data || []).catch(() => []),
            api.get(`/cases/${caseId}`).then((r) => r.data?.plan || {}).catch(() => ({})),
        ]).then(([revs, plan]) => {
            if (!alive) return;
            setRequests(revs);
            setPlanSnapshot(plan);
            // Restore previously-actioned items if the planner has
            // partially acknowledged this revision on another tab.
            const latest = [...revs].sort((a, b) => (b.iteration || 0) - (a.iteration || 0))[0];
            if (latest && Array.isArray(latest.actioned_items) && latest.actioned_items.length) {
                setActioned(new Set(latest.actioned_items));
            }
        }).finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [caseId]);

    // Sort newest first; the latest is what the planner is reworking.
    const sorted = useMemo(
        () => [...requests].sort((a, b) => (b.iteration || 0) - (a.iteration || 0)),
        [requests]
    );

    if (loading || !requests.length) return null;

    const latest = sorted[0];
    const older = sorted.slice(1);
    const isClosed = latest.status === "actioned";

    // Build the row list. Each row carries a stable `key` used in the
    // `actioned` Set and posted back to the server.
    const structRows = Object.entries(latest.structured_changes || {})
        .filter(([, v]) => v !== null && v !== undefined && v !== "")
        .map(([field, value]) => ({
            kind: "struct",
            key: `struct.${field}`,
            field,
            requested: value,
            meta: STRUCT_FIELDS[field] || { label: field, unit: "", planKey: null, section: "Other" },
        }));
    const itemRows = (latest.items || []).map((it) => ({
        kind: "item",
        key: `item.${it.category}`,
        item: it,
    }));
    const totalRows = structRows.length + itemRows.length;

    const sortedItems = sortItemsBySeverity(itemRows.map((r) => r.item));
    const criticalCount = sortedItems.filter((i) => i.severity === "critical").length;

    const canPlanner = (user?.role === "staff") &&
        ["planner", "coe_supervisor", "supervisor"].includes((user?.staff_type || "").toLowerCase());
    const canResubmit = canPlanner
        && !isClosed
        && totalRows > 0
        && actioned.size >= totalRows
        && ["revision_requested", "in_planning"].includes(caseStatus);

    const toggleRow = (key) => {
        if (isClosed) return;
        setActioned((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    const handleResubmit = async () => {
        if (!canResubmit) return;
        setSubmitting(true);
        try {
            /* iter98.88 — Send the planner's reply note when one is
             * authored. Empty textarea falls back to the legacy
             * system-style stamp so the audit timeline never has a
             * blank entry. */
            const noteToSend = replyNote.trim()
                ? replyNote.trim()
                : `Planner resubmitted iteration ${latest.iteration || 1}.`;
            await api.post(
                `/cases/${caseId}/revision-requests/${latest.id}/resubmit`,
                {
                    actioned_items: Array.from(actioned),
                    note: noteToSend,
                },
            );
            toast.success("Revised plan sent to doctor");
            onResubmit?.();
            // Optimistically mark the latest entry as actioned so the UI
            // updates immediately without waiting for the parent reload.
            setRequests((prev) =>
                prev.map((r) => r.id === latest.id ? { ...r, status: "actioned" } : r)
            );
            // Clear the textarea so subsequent re-renders don't show stale text.
            setReplyNote("");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to resubmit");
        } finally {
            setSubmitting(false);
        }
    };

    // Group structured rows by section for visual grouping.
    const structBySection = structRows.reduce((acc, row) => {
        const s = row.meta.section || "Other";
        if (!acc[s]) acc[s] = [];
        acc[s].push(row);
        return acc;
    }, {});

    return (
        <div className="rounded-2xl border-2 border-rose-200 bg-rose-50/40 overflow-hidden" data-testid="planner-revision-panel">
            {/* Header — always visible. Sets the scene for the planner. */}
            <div className="px-5 py-4 border-b border-rose-200/70 bg-rose-100/40 flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-full bg-rose-200 grid place-items-center shrink-0 mt-0.5">
                        <ArrowUUpLeft size={18} weight="fill" className="text-rose-700" />
                    </div>
                    <div className="min-w-0">
                        <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-rose-700">
                            Doctor&apos;s revision request · Iteration {latest.iteration || 1}
                        </div>
                        <h3 className="font-display text-base font-bold text-rose-900 truncate">
                            {latest.requested_by_name || "Doctor"} requested {totalRows} change{totalRows !== 1 ? "s" : ""}
                        </h3>
                        <div className="text-xs text-rose-900/80 mt-0.5">
                            {fmtRelative(latest.requested_at)}
                            {criticalCount > 0 && (
                                <span className="ml-2 font-bold text-rose-700">· {criticalCount} critical</span>
                            )}
                            {older.length > 0 && (
                                <span className="ml-2 text-rose-700/60">· {older.length} earlier iteration{older.length !== 1 ? "s" : ""}</span>
                            )}
                            {isClosed && (
                                <span className="ml-2 font-bold text-emerald-700 inline-flex items-center gap-0.5">
                                    · <CheckCircle size={11} weight="fill" /> Actioned
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {canPlanner && !isClosed && (
                        <span
                            className="text-[10px] font-bold tracking-wider uppercase px-2 py-1 rounded-full bg-white border border-rose-300 text-rose-800"
                            data-testid="planner-revision-progress"
                        >
                            {actioned.size} / {totalRows} actioned
                        </span>
                    )}
                    <button
                        type="button"
                        onClick={() => setExpanded((v) => !v)}
                        className="text-[11px] font-semibold text-rose-700 hover:text-rose-900 px-3 py-1.5 rounded-full hover:bg-rose-200/60 transition-colors"
                        data-testid="planner-revision-toggle"
                    >
                        {expanded ? "Collapse" : "Show details"}
                    </button>
                </div>
            </div>

            {expanded && (
                <div className="p-5 space-y-4">
                    {/* Doctor's overall summary — the gist */}
                    {latest.summary && (
                        <div className="rounded-xl border border-rose-200 bg-white p-3.5" data-testid="planner-revision-summary">
                            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-rose-700 mb-1">Overall summary</div>
                            <p className="text-sm text-foreground/90 leading-snug whitespace-pre-wrap">{latest.summary}</p>
                        </div>
                    )}

                    {/* Structured changes — diff table grouped by section */}
                    {Object.keys(structBySection).map((section) => (
                        <StructuredSection
                            key={section}
                            section={section}
                            rows={structBySection[section]}
                            plan={planSnapshot || {}}
                            actioned={actioned}
                            onToggle={toggleRow}
                            disabled={isClosed}
                            canPlanner={canPlanner}
                        />
                    ))}

                    {/* Free-text items — categorised */}
                    {sortedItems.length > 0 && (
                        <div className="space-y-2" data-testid="planner-revision-items">
                            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">
                                Free-text revisions ({sortedItems.length})
                            </div>
                            {sortedItems.map((it, idx) => {
                                const cat = CATEGORY_LOOKUP[it.category] || CATEGORY_LOOKUP.other;
                                const sev = SEVERITY_CONFIG[it.severity] || SEVERITY_CONFIG.normal;
                                const CIcon = cat.icon;
                                const key = `item.${it.category}`;
                                const checked = actioned.has(key);
                                return (
                                    <div
                                        key={`${it.category}-${idx}`}
                                        className={`rounded-lg border bg-white border-rose-100 p-3 flex gap-3 ${checked ? "opacity-60" : ""}`}
                                        data-testid={`planner-revision-item-${it.category}`}
                                    >
                                        <div className="w-8 h-8 rounded-md bg-rose-100 text-rose-700 grid place-items-center shrink-0 mt-0.5">
                                            <CIcon size={15} weight="duotone" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-1.5 flex-wrap mb-1">
                                                <span className="text-xs font-bold text-foreground">{cat.label}</span>
                                                <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${sev.chip}`}>
                                                    {sev.label}
                                                </span>
                                                {teethLabel(it.teeth) && (
                                                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-secondary text-foreground/70" title={it.teeth.join(", ")}>
                                                        {teethLabel(it.teeth)}
                                                    </span>
                                                )}
                                            </div>
                                            <p className={`text-sm leading-snug whitespace-pre-wrap ${checked ? "line-through text-muted-foreground" : "text-foreground/90"}`}>
                                                {it.note}
                                            </p>
                                        </div>
                                        {canPlanner && (
                                            <label className="flex items-center gap-1.5 text-[11px] font-semibold text-rose-900/80 cursor-pointer select-none shrink-0">
                                                <Checkbox
                                                    checked={checked}
                                                    onCheckedChange={() => toggleRow(key)}
                                                    disabled={isClosed}
                                                    data-testid={`planner-actioned-checkbox-item-${it.category}`}
                                                />
                                                {checked ? "Actioned" : "Mark as actioned"}
                                            </label>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Resubmit footer — appears for the planner once the
                        doctor has any rows. Greyed out until every row is
                        ticked. */}
                    {canPlanner && totalRows > 0 && !isClosed && (
                        <ResubmitFooter
                            actionedCount={actioned.size}
                            total={totalRows}
                            canResubmit={canResubmit}
                            onResubmit={handleResubmit}
                            submitting={submitting}
                            caseStatus={caseStatus}
                            replyNote={replyNote}
                            onReplyNoteChange={setReplyNote}
                        />
                    )}

                    {/* Earlier iterations — collapsed by default. */}
                    {older.length > 0 && (
                        <EarlierIterations older={older} plan={planSnapshot || {}} />
                    )}
                </div>
            )}
        </div>
    );
}


/* ─────────── StructuredSection ──────────────────────────────────── */

function StructuredSection({ section, rows, plan, actioned, onToggle, disabled, canPlanner }) {
    if (!rows?.length) return null;
    return (
        <div className="space-y-2" data-testid={`planner-struct-section-${section.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">
                {section}
            </div>
            <div className="rounded-lg border border-rose-100 bg-white overflow-hidden">
                {rows.map((row) => {
                    const checked = actioned.has(row.key);
                    return (
                        <StructuredDiffRow
                            key={row.key}
                            row={row}
                            plan={plan}
                            checked={checked}
                            onToggle={() => onToggle(row.key)}
                            disabled={disabled}
                            canPlanner={canPlanner}
                        />
                    );
                })}
            </div>
        </div>
    );
}


function StructuredDiffRow({ row, plan, checked, onToggle, disabled, canPlanner }) {
    const { meta, requested } = row;
    const current = meta.planKey ? plan[meta.planKey] : undefined;
    const fmt = (v) => {
        if (v === undefined || v === null || v === "") return "—";
        if (meta.unit) return `${v} ${meta.unit}`;
        return String(v);
    };
    const Icon = meta.icon || NotePencil;
    return (
        <div
            className={`flex items-center justify-between gap-3 px-3 py-2.5 border-b border-rose-100 last:border-b-0 ${checked ? "bg-emerald-50/40" : ""}`}
            data-testid={`planner-struct-row-${row.field}`}
        >
            <div className="flex items-center gap-2 min-w-0">
                <div className="w-7 h-7 rounded bg-rose-100 text-rose-700 grid place-items-center shrink-0">
                    <Icon size={13} weight="duotone" />
                </div>
                <span className="text-xs font-bold text-foreground/90 truncate">{meta.label}</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
                <span className={`font-mono text-xs px-2 py-0.5 rounded bg-secondary text-foreground/70 ${checked ? "line-through opacity-60" : ""}`}>
                    {fmt(current)}
                </span>
                <span className="text-rose-600 text-base leading-none">→</span>
                <span className={`font-mono text-xs px-2 py-0.5 rounded font-bold ${checked ? "bg-emerald-100 text-emerald-900" : "bg-amber-100 text-amber-900"}`}>
                    {fmt(requested)}
                </span>
                {canPlanner && (
                    <Checkbox
                        checked={checked}
                        onCheckedChange={onToggle}
                        disabled={disabled}
                        className="shrink-0 ml-1"
                        data-testid={`planner-actioned-checkbox-struct-${row.field}`}
                    />
                )}
            </div>
        </div>
    );
}


/* ─────────── ResubmitFooter ─────────────────────────────────────── */

function ResubmitFooter({
    actionedCount, total, canResubmit, onResubmit, submitting, caseStatus,
    // iter98.88 — new props for the reply-note textarea. Lifted to the
    // parent so the value survives accordion collapse + page tab swaps.
    replyNote = "", onReplyNoteChange,
}) {
    const allDone = actionedCount >= total;
    /* Smooth-scroll the planner into the standard plan-editor section
     * so they don't have to hunt for it. The PlanEditorInline mounts
     * under `id="sec-wear"` (the first authoring accordion) and also
     * listens for the `plan-section-open` event so the section gets
     * expanded automatically. */
    const openPlanEditor = () => {
        window.dispatchEvent(
            new CustomEvent("plan-section-open", { detail: { sectionId: "sec-wear" } })
        );
        setTimeout(() => {
            const target = document.getElementById("sec-wear")
                || document.querySelector('[data-testid="planner-workspace"]')
                || document.querySelector('[data-testid="plan-readiness-scorecard"]');
            if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 80);
    };
    return (
        <div
            className="mt-3 rounded-xl border border-rose-200 bg-white p-3.5 space-y-3"
            data-testid="planner-resubmit-footer"
        >
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs text-foreground/80">
                    {allDone ? (
                        <span className="font-bold text-emerald-700 inline-flex items-center gap-1">
                            <CheckCircle size={13} weight="fill" /> All {total} changes actioned — ready to resubmit
                        </span>
                    ) : (
                        <span>
                            Tick every row you&apos;ve addressed in the plan, then click <span className="font-bold">Resubmit</span> to send the revised plan back to the doctor.
                            <span className="ml-1 text-muted-foreground">({total - actionedCount} pending)</span>
                        </span>
                    )}
                </div>
                <button
                    type="button"
                    onClick={openPlanEditor}
                    className="text-[11px] font-bold text-rose-700 hover:text-rose-900 underline-offset-4 hover:underline inline-flex items-center gap-1"
                    data-testid="planner-open-plan-editor-btn"
                >
                    Open plan editor <NotePencil size={12} weight="bold" />
                </button>
            </div>

            {/* iter98.88 — Reply note composer. Optional but encouraged —
                lets the planner explain clinical reasoning when one of
                the doctor's asks needed a counter-suggestion. Empty
                falls back to the system-style stamp on submit. */}
            <div>
                <label className="text-[10.5px] font-bold tracking-[0.12em] uppercase text-rose-800/80 flex items-center gap-1 mb-1.5">
                    <NotePencil size={11} weight="duotone" />
                    Reply to the doctor (optional)
                </label>
                <Textarea
                    value={replyNote}
                    onChange={(e) => onReplyNoteChange?.(e.target.value)}
                    placeholder="e.g. Widened the upper arch as requested. Couldn't reduce the IPR on 13-14 below 0.3mm without compromising the rotation — flagged for your sign-off."
                    rows={3}
                    className="text-sm bg-secondary/30"
                    data-testid="planner-reply-note"
                    disabled={submitting}
                />
                <p className="text-[10px] text-muted-foreground mt-1">
                    The doctor will see this note alongside your actioned items when the case returns to their queue.
                </p>
            </div>

            <div className="flex justify-end">
                <Button
                    onClick={onResubmit}
                    disabled={!canResubmit || submitting}
                    className="bg-rose-700 hover:bg-rose-800 text-white rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="planner-resubmit-btn"
                    title={
                        !allDone
                            ? `${total - actionedCount} row${total - actionedCount === 1 ? "" : "s"} still need actioning`
                            : caseStatus === "revision_requested"
                                ? "Resubmit the revised plan to the doctor"
                                : "Send revised plan to doctor"
                    }
                >
                    {submitting ? "Resubmitting…" : <>Resubmit to doctor <PaperPlaneTilt size={13} weight="fill" className="ml-1.5" /></>}
                </Button>
            </div>
        </div>
    );
}


/* ─────────── EarlierIterations ──────────────────────────────────── */

function EarlierIterations({ older, plan }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="pt-2 border-t border-rose-200/60">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="text-[11px] font-bold tracking-wider uppercase text-rose-700/80 hover:text-rose-900"
                data-testid="planner-revision-earlier-toggle"
            >
                {open ? "Hide" : "Show"} {older.length} earlier iteration{older.length !== 1 ? "s" : ""}
            </button>
            {open && (
                <div className="mt-3 space-y-3">
                    {older.map((req) => (
                        <div key={req.id} className="rounded-lg border border-rose-100 bg-white p-3 opacity-90">
                            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-rose-700/80 mb-1 flex items-center justify-between gap-2 flex-wrap">
                                <span>Iteration {req.iteration} · {fmtRelative(req.requested_at)} · {req.requested_by_name || "Doctor"}</span>
                                {req.status === "actioned" && (
                                    <span className="text-[10px] font-bold text-emerald-700 inline-flex items-center gap-1 normal-case tracking-normal">
                                        <CheckCircle size={11} weight="fill" /> Actioned
                                        {req.actioned_by_name ? ` by ${req.actioned_by_name}` : ""}
                                    </span>
                                )}
                            </div>
                            {req.summary && <p className="text-sm text-foreground/85 mb-2 leading-snug">{req.summary}</p>}
                            {/* Struct changes (older) — compact */}
                            {Object.keys(req.structured_changes || {}).length > 0 && (
                                <ul className="text-xs space-y-0.5 ml-3 list-disc text-foreground/75 mb-2">
                                    {Object.entries(req.structured_changes).map(([f, v]) => {
                                        const meta = STRUCT_FIELDS[f] || { label: f, unit: "" };
                                        const cur = meta.planKey ? plan[meta.planKey] : undefined;
                                        return (
                                            <li key={f}>
                                                <span className="font-medium">{meta.label}:</span>{" "}
                                                <span className="font-mono">{cur ?? "—"}</span>
                                                {" → "}
                                                <span className="font-mono font-bold">{v}{meta.unit ? ` ${meta.unit}` : ""}</span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                            {req.items?.length > 0 && (
                                <ul className="text-xs space-y-0.5 ml-3 list-disc text-foreground/75">
                                    {req.items.map((it, idx) => (
                                        <li key={idx}>
                                            <span className="font-medium">{CATEGORY_LOOKUP[it.category]?.label || it.category}:</span>{" "}
                                            {it.note}
                                            {it.teeth?.length > 0 && <span className="text-muted-foreground"> ({it.teeth.join(", ")})</span>}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}


function sortItemsBySeverity(items) {
    const order = { critical: 0, normal: 1, minor: 2 };
    return [...items].sort((a, b) => (order[a.severity] ?? 1) - (order[b.severity] ?? 1));
}
